import React from 'react';
import { Box, MenuItem, Select, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { CaseWorkflowDimension, FilterOptionsResponse } from '../../services/caseWorkflowService';
import { CASE_STATUS_LABELS, NOT_CLOSED_CAPTION, NOT_CLOSED_SINK_CAVEAT } from './caseWorkflowVocabulary';
import { WINDOWS } from './caseWorkflowWindows';
import { control, menu } from './filterStyles';
import { dsVar, ink } from '../../theme/tokens';

export interface FilterState {
    window: string;
    flow_code: string;
    run_id: string;
    dimension: string;
    value: string;
    transition_code: string;
    workflow_state: string;
    case_status: string;
}

interface CaseWorkflowFiltersProps {
    state: FilterState;
    onChange: (patch: Partial<FilterState>) => void;
    options: FilterOptionsResponse | null;
    dimensions: CaseWorkflowDimension[];
}

const humanise = (code: string) => code.replace(/_/g, ' ').toLowerCase()
    .replace(/^./, (c) => c.toUpperCase());

/**
 * One filter bar, whose state is handed to every endpoint unchanged.
 *
 * There is deliberately one of these rather than a control per panel: every row
 * on the screen answers the same question about the same population, and the
 * drill-down guarantee only holds while they all share a predicate.
 */
const CaseWorkflowFilters: React.FC<CaseWorkflowFiltersProps> = ({ state, onChange, options, dimensions }) => {
    const dimension = dimensions.find((d) => d.code === state.dimension);

    return (
        <Box className="flex flex-wrap items-center gap-3">
            <Select
                value={state.window}
                onChange={(e) => onChange({ window: String(e.target.value) })}
                size="small"
                sx={control}
                MenuProps={menu}
            >
                {WINDOWS.map((w) => (
                    <MenuItem key={w.key} value={w.key} sx={{ fontSize: '0.8125rem' }}>{w.label}</MenuItem>
                ))}
            </Select>

            {/*
              * The population, beside the window because it is the same kind of
              * control: both say which cases the screen is about, before any
              * question is asked of them.
              *
              * A picker rather than a chip, unlike workflow_state below. That one
              * only ever arrives by clicking a census bar, so it has something to
              * clear; this one has a default the reader has to be able to get
              * back to without knowing what they clicked.
              */}
            <Box className="flex items-center gap-1">
                <Select
                    value={state.case_status || 'all'}
                    onChange={(e) => onChange({ case_status: String(e.target.value) })}
                    size="small"
                    sx={control}
                    MenuProps={menu}
                >
                    {Object.entries(CASE_STATUS_LABELS).map(([code, label]) => (
                        <MenuItem key={code} value={code} sx={{ fontSize: '0.8125rem' }}>{label}</MenuItem>
                    ))}
                </Select>
                {state.case_status === 'not_closed' && (
                    <Tooltip title={`${NOT_CLOSED_CAPTION} ${NOT_CLOSED_SINK_CAVEAT}`} arrow placement="bottom">
                        <InfoOutlinedIcon sx={{ fontSize: 14, color: ink('#FBBF24'), cursor: 'help' }} />
                    </Tooltip>
                )}
            </Box>

            <Box className="flex items-center gap-1">
                <Select
                    value={state.flow_code}
                    onChange={(e) => onChange({ flow_code: String(e.target.value) })}
                    size="small"
                    displayEmpty
                    sx={control}
                    MenuProps={menu}
                >
                    <MenuItem value="" sx={{ fontSize: '0.8125rem' }}>All flows</MenuItem>
                    {(options?.flows ?? []).map((flow) => (
                        <MenuItem key={flow.flowCode} value={flow.flowCode} sx={{ fontSize: '0.8125rem' }}>
                            {humanise(flow.flowCode)}
                        </MenuItem>
                    ))}
                </Select>
                {/*
                  * Root flows only. flow_code is stamped once with the root
                  * agent's code and inherited by every sub-graph, so offering a
                  * sub-flow would return an empty screen.
                  */}
                <Tooltip
                    title="Top-level flows only. A step that runs inside another flow is recorded under its parent, so it cannot be selected separately."
                    arrow
                >
                    <InfoOutlinedIcon sx={{ fontSize: 14, color: dsVar('fg-muted'), cursor: 'help' }} />
                </Tooltip>
            </Box>

            {/*
              * Offered only when the window actually spans more than one run,
              * because a control with a single option is noise.
              */}
            {(options?.runs?.length ?? 0) > 1 && (
                <Select
                    value={state.run_id}
                    onChange={(e) => onChange({ run_id: String(e.target.value) })}
                    size="small"
                    displayEmpty
                    sx={control}
                    MenuProps={menu}
                >
                    <MenuItem value="" sx={{ fontSize: '0.8125rem' }}>
                        All {options?.runs.length} runs
                    </MenuItem>
                    {(options?.runs ?? []).map((run, index) => (
                        <MenuItem key={run.runId} value={run.runId} sx={{ fontSize: '0.8125rem' }}>
                            Run {index + 1} — {new Date(run.firstAt).toLocaleDateString()}
                        </MenuItem>
                    ))}
                </Select>
            )}

            {/*
              * Built from contract.dimensions. No dimension code is written as a
              * literal anywhere in this component, so one added upstream reaches
              * the picker without a UI change.
              */}
            <Select
                value={state.dimension}
                onChange={(e) => onChange({ dimension: String(e.target.value), value: '' })}
                size="small"
                sx={control}
                MenuProps={menu}
            >
                {dimensions.map((d) => (
                    <MenuItem key={d.code} value={d.code} sx={{ fontSize: '0.8125rem' }}>
                        Break down by {d.label.toLowerCase()}
                    </MenuItem>
                ))}
            </Select>

            {state.value && dimension && (
                <Box
                    className="flex items-center gap-1.5 rounded-full border border-accent-fg/40 bg-accent/10 px-3 py-1 cursor-pointer"
                    onClick={() => onChange({ value: '' })}
                >
                    <Typography sx={{ fontSize: '0.75rem', color: dsVar('accent-fg') }}>
                        {dimension.label}: {state.value.replace(/_/g, ' ')}
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: dsVar('accent-fg') }}>×</Typography>
                </Box>
            )}

            {state.workflow_state && (
                <Box
                    className="flex items-center gap-1.5 rounded-full border border-accent-fg/40 bg-accent/10 px-3 py-1 cursor-pointer"
                    onClick={() => onChange({ workflow_state: '' })}
                >
                    <Typography sx={{ fontSize: '0.75rem', color: dsVar('accent-fg') }}>
                        Sitting in: {humanise(state.workflow_state)}
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: dsVar('accent-fg') }}>×</Typography>
                </Box>
            )}

            {state.transition_code && (
                <Box
                    className="flex items-center gap-1.5 rounded-full border border-accent-fg/40 bg-accent/10 px-3 py-1 cursor-pointer"
                    onClick={() => onChange({ transition_code: '' })}
                >
                    <Typography sx={{ fontSize: '0.75rem', color: dsVar('accent-fg') }}>
                        Step: {humanise(state.transition_code)}
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: dsVar('accent-fg') }}>×</Typography>
                </Box>
            )}
        </Box>
    );
};

export default CaseWorkflowFilters;
